import React from 'react'
import { Box, Textarea, type Sx } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import AppInput from './AppInput'

/** @deprecated Prefer CommentInputRow; kept for legacy Group compositions. */
export const standardInputFlexGroupSx: Sx = {
  flexWrap: 'nowrap',
  width: '100%',
  alignItems: 'flex-end',
  '& > *:first-of-type': { flex: '1 1 0', minWidth: 0, maxWidth: '100%' }
}

const flexFillStyles: Sx = {
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  alignSelf: 'stretch'
}

const gridFillStyles: Sx = {
  minWidth: 0,
  maxWidth: '100%',
  width: '100%'
}

const fullWidthTextareaStyles = {
  root: { width: '100%', maxWidth: '100%' },
  wrapper: { width: '100%', maxWidth: '100%' },
  input: { width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' as const }
}

export interface StandardInputProps {
  placeholder?: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  disabled?: boolean;
  label?: string;
  error?: React.ReactNode;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  autosize?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  sx?: Sx;
  /** Use `grid` inside CommentInputRow; `flex` for legacy Mantine Group rows. */
  fillMode?: 'flex' | 'grid';
}

/**
 * Full-width Mantine input for comment bars and reply rows.
 */
export default function StandardInput ({
  placeholder,
  value,
  onChange,
  disabled,
  label,
  error,
  rows,
  minRows,
  maxRows,
  autosize,
  onKeyDown,
  sx,
  fillMode = 'flex'
}: StandardInputProps) {
  const mobile = useMediaQuery('(max-width: 768px)')
  const size = mobile ? 'xs' : 'sm'
  const extraSx = sx ? (Array.isArray(sx) ? sx : [sx]) : []
  const fillStyles = fillMode === 'grid' ? gridFillStyles : flexFillStyles

  const useTextarea =
    autosize === true ||
    (rows != null && rows > 1) ||
    (minRows != null && minRows > 1)

  if (useTextarea) {
    return (
      <Box sx={[fillStyles, ...extraSx]}>
        <Textarea
          radius="sm"
          size={size}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          label={label}
          error={error}
          rows={rows}
          minRows={minRows ?? rows ?? 1}
          maxRows={maxRows}
          autosize={autosize}
          onKeyDown={onKeyDown}
          w="100%"
          styles={fullWidthTextareaStyles}
        />
      </Box>
    )
  }

  return (
    <Box sx={[fillStyles, ...extraSx]}>
      <AppInput
        radius="sm"
        size={size}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        label={label}
        error={error}
        onKeyDown={onKeyDown}
        w="100%"
        styles={{ root: fullWidthTextareaStyles.root, input: fullWidthTextareaStyles.input }}
      />
    </Box>
  )
}
